import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { listAuditEvents, type AuditEvent } from '../api/audit';
import { DataTable, type Column } from './common/DataTable';

export function AuditLogView() {
  const { getJwtToken } = useAuth();

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Default date range: last 7 days
  const today = new Date();
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(sevenDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listAuditEvents(startDate, endDate, token, 200);
      setEvents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken, startDate, endDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Auto-refresh toggle
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchEvents();
      }, 30_000); // 30 seconds
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, fetchEvents]);

  const columns: Column<AuditEvent>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      render: (e) => new Date(e.timestamp).toLocaleString(),
    },
    { key: 'actor', header: 'Actor' },
    {
      key: 'action',
      header: 'Action',
      render: (e) => <code>{e.action}</code>,
    },
    { key: 'target', header: 'Target' },
    { key: 'details', header: 'Details' },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Audit Log</h1>
        <div className="view__actions">
          <label className="view__toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span style={{ marginLeft: 4 }}>Auto-refresh</span>
          </label>
          <button
            className="btn btn--secondary"
            onClick={fetchEvents}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Date range filter */}
      <div className="view__filters" style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-group__label">Start Date</label>
          <input
            className="form-group__input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-group__label">End Date</label>
          <input
            className="form-group__input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && events.length === 0 ? (
        <div className="view__empty">Loading audit events...</div>
      ) : (
        <DataTable<AuditEvent>
          columns={columns}
          data={events}
          keyExtractor={(e) => e.eventId}
          emptyMessage="No audit events found for the selected date range."
        />
      )}
    </div>
  );
}
